import { useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Paper,
  Popover,
  TextField,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate } from 'react-router-dom';
import { useWorkspace } from '../store/WorkspaceContext';
import { buildFolderPath, getFileDisplayName } from '../utils/workspace';

function SearchAutocomplete({
  autoFocus = false,
  inputValue,
  openOnFocus = true,
  onInputChange,
  onSelect,
  options,
  sx = {},
}) {
  return (
    <Autocomplete
      size="small"
      openOnFocus={openOnFocus}
      options={options}
      value={null}
      inputValue={inputValue}
      onInputChange={(_, nextValue) => onInputChange(nextValue)}
      onChange={onSelect}
      getOptionLabel={(option) => option.label}
      groupBy={(option) => (option.type === 'folder' ? 'Folders' : 'Files')}
      noOptionsText="No matches"
      sx={{
        width: { xs: 180, sm: 260, md: 380 },
        flexShrink: 0,
        '& .MuiOutlinedInput-root': {
          height: 42,
          borderRadius: 1,
          bgcolor: 'background.paper',
        },
        ...sx,
      }}
      renderInput={(params) => {
        const inputSlotProps = params.slotProps?.input || {};

        return (
          <TextField
            {...params}
            autoFocus={autoFocus}
            placeholder="Search folders and files"
            slotProps={{
              ...params.slotProps,
              input: {
                ...inputSlotProps,
                startAdornment: (
                  <>
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                    {inputSlotProps.startAdornment}
                  </>
                ),
              },
            }}
          />
        );
      }}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;

        return (
          <Box component="li" key={key} {...optionProps}>
            <ListItemIcon sx={{ minWidth: 34 }}>
              {option.type === 'folder' ? <FolderIcon /> : <InsertDriveFileIcon />}
            </ListItemIcon>
            <ListItemText
              primary={option.label}
              secondary={option.path}
              primaryTypographyProps={{ noWrap: true }}
              secondaryTypographyProps={{ noWrap: true }}
            />
          </Box>
        );
      }}
    />
  );
}

export default function WorkspaceSearch({ sx = {} }) {
  const navigate = useNavigate();
  const { currentFolderId, folders, files, searchIndex, setCurrentFolderId } = useWorkspace();
  const theme = useTheme();
  const compactSearch = useMediaQuery(theme.breakpoints.down('lg'));
  const [inputValue, setInputValue] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const currentLevelOptions = useMemo(() => {
    const currentPath = buildFolderPath(folders, currentFolderId);
    const folderOptions = folders
      .filter((folder) => folder.parentId === currentFolderId)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((folder) => ({
        id: folder.id,
        type: 'folder',
        label: folder.name,
        parentId: folder.parentId,
        path: currentPath,
      }));
    const fileOptions = files
      .filter((file) => file.parentFolderId === currentFolderId)
      .sort((a, b) => getFileDisplayName(a).localeCompare(getFileDisplayName(b)))
      .map((file) => ({
        id: file.id,
        type: 'file',
        label: getFileDisplayName(file),
        parentId: file.parentFolderId,
        path: currentPath,
      }));

    return [...folderOptions, ...fileOptions];
  }, [currentFolderId, files, folders]);
  const globalOptions = useMemo(
    () =>
      [...searchIndex].sort((a, b) => {
        if (a.type !== b.type) {
          return a.type === 'folder' ? -1 : 1;
        }

        return a.label.localeCompare(b.label);
      }),
    [searchIndex]
  );
  const options = inputValue.trim() ? globalOptions : currentLevelOptions;
  const compactOpen = Boolean(anchorEl);

  const handleSelect = (_, option) => {
    if (!option) {
      return;
    }

    if (option.type === 'folder') {
      setCurrentFolderId(option.id);
      navigate('/');
    } else {
      setCurrentFolderId(option.parentId);
      navigate(`/file/${option.id}`);
    }

    setInputValue('');
    setAnchorEl(null);
  };

  if (compactSearch) {
    return (
      <>
        <Paper
          variant="outlined"
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 0.5,
            borderRadius: 1,
            bgcolor: 'background.paper',
            flexShrink: 0,
            height: 42,
            userSelect: 'none',
            ...sx,
          }}
        >
          <Tooltip title="Search">
            <IconButton
              aria-label="Search folders and files"
              onClick={(event) => setAnchorEl(event.currentTarget)}
              sx={{
                width: 36,
                height: 36,
                borderRadius: 0.75,
                color: compactOpen ? 'primary.main' : 'text.secondary',
                bgcolor: compactOpen ? 'action.selected' : 'transparent',
                '&:hover': {
                  color: 'primary.main',
                  bgcolor: 'action.hover',
                },
              }}
            >
              <SearchIcon />
            </IconButton>
          </Tooltip>
        </Paper>

        <Popover
          open={compactOpen}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{
            paper: {
              sx: {
                mt: 1,
                width: { xs: 'calc(100vw - 32px)', sm: 420, md: 480 },
                maxWidth: 'calc(100vw - 32px)',
                borderRadius: 1,
                p: 1,
              },
            },
          }}
        >
          <SearchAutocomplete
            autoFocus
            inputValue={inputValue}
            openOnFocus={false}
            onInputChange={setInputValue}
            onSelect={handleSelect}
            options={options}
            sx={{ width: '100%' }}
          />
        </Popover>
      </>
    );
  }

  return (
    <SearchAutocomplete
      inputValue={inputValue}
      onInputChange={setInputValue}
      onSelect={handleSelect}
      options={options}
      sx={sx}
    />
  );
}
